import * as Sentry from '@sentry/react';

import { ENV } from '../../config/env.js';

/**
 * Initialize Sentry error tracking.
 * Does nothing if VITE_SENTRY_DSN is not configured.
 *
 * Call this as early as possible during application startup.
 */
export function initSentry(): void {
  const dsn = ENV.sentry.dsn;

  if (dsn === undefined) {
    // Sentry DSN not configured, skip initialization
    console.warn('Sentry DSN not configured, skipping initialization');
    return;
  }

  Sentry.init({
    dsn,
    environment: ENV.appEnv,
    enabled: true,
    enableLogs: true,

    // Performance monitoring
    tracesSampleRate: 1.0,
    tracePropagationTargets: ['localhost', '127.0.0.1', /^https:\/\/.*\.lotushealth\.(ai|com)/],

    // Session replay for error context
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.httpClientIntegration({
        // Capture 5xx errors as Sentry events
        failedRequestStatusCodes: [[500, 599]],
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
  });
}

/**
 * Capture an exception to Sentry.
 * Safe to call even if Sentry is not initialized.
 */
export function captureException(error: unknown, context?: Record<string, unknown>): void {
  Sentry.captureException(error, { extra: context });
}

/**
 * Capture a message to Sentry.
 * Safe to call even if Sentry is not initialized.
 */
export function captureMessage(message: string, context?: Record<string, unknown>): void {
  Sentry.captureMessage(message, { extra: context });
}

/**
 * Set the current user context for Sentry.
 * Call this when the user logs in.
 */
export interface SentryUserContext {
  firebaseUserId: string;
  backendUserId?: string;
  phoneNumber?: string;
}

export function setUser(user: SentryUserContext | null): void {
  if (user === null) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: user.backendUserId ?? user.firebaseUserId,
    firebaseUserId: user.firebaseUserId,
    backendUserId: user.backendUserId,
    phoneNumber: user.phoneNumber,
  });
}
